/**
 * Created by razial on 22.07.2026.
 */

const ShadowNode = function (shadowNodeId) {
    this.shadowNodeId = shadowNodeId;

    this.parent = null;
    this.children = [];

    this.type = undefined;
    this.params = {};
    this.properties = {};
    this.components = {};

    this.nodes = [];
};

ShadowNode.prototype.isRoot = function () {
    return this.parent === "root";
};

ShadowNode.prototype.getChildren = function () {
    return this.children;
};

ShadowNode.prototype.initProperties = function (config) {
    const properties = { ...config };

    delete properties.children;
    delete properties.shadowNodeId;
    delete properties.parent;
    delete properties.components;

    this.type = config.type || "node";

    this.properties = cleverapps.clone(properties, true);
    this.components = cleverapps.clone(config.components, true) || {};
};

ShadowNode.prototype.initChildren = function (children) {
    this.children = children;
};

ShadowNode.prototype.initParent = function (parent) {
    this.parent = parent;
};

ShadowNode.prototype.initParams = function (params) {
    this.params = cleverapps.clone(params, true);
};

ShadowNode.prototype.addInstance = function (node) {
    const index = this.nodes.indexOf(node);
    if (index === -1) {
        this.nodes.push(node);
    }
};

ShadowNode.prototype.removeInstance = function (node) {
    const index = this.nodes.indexOf(node);
    if (index !== -1) {
        this.nodes.splice(index, 1);
    }
};

ShadowNode.prototype.listNodes = function () {
    return this.nodes;
};

ShadowNode.prototype.rebuild = function () {
    const data = this.nodes.map((node) => ({
        node,
        parent: node.getParent(),
        arrivalOrder: node.getOrderOfArrival(),
        context: ComponentMap.get(node, ShadowNodeBridge).getContext()
    }));

    this.nodes.length = 0;

    data.forEach(({ node, parent }) => {
        if (parent) {
            node.removeFromParent();
        } else {
            node.cleanup();
        }
    });

    data.forEach(({ parent, context, arrivalOrder }) => {
        if (parent) {
            const node = PrefabBuilder.buildNode(this.serializeContent(), context);
            if (node) {
                parent.addChild(node);
                node.setOrderOfArrival(arrivalOrder);
                parent.sortAllChildren();
            }
        }
    });
};

ShadowNode.prototype.calculate = function () {
    const resolveValue = (object, getter) => (typeof getter === "function" ? getter(object) : getter);

    const result = {
        shadowNodeId: this.shadowNodeId,

        parent: this.parent,
        children: this.children,

        type: this.type,
        properties: {},
        components: {},
        params: {}
    };

    const node = this.nodes[0];

    for (const name in Prefab.PROPERTIES[this.type]) {
        const declaration = Prefab.PROPERTIES[this.type][name];
        const record = {
            target: this.properties[name]
        };

        if (record.target === undefined && declaration.default !== undefined) {
            record.target = resolveValue(node, declaration.default);
        }

        if (record.target === undefined && declaration.editorDefault !== undefined) {
            record.target = resolveValue(node, declaration.editorDefault);
        }

        if (declaration.getter && node) {
            record.actual = declaration.getter(node);
        }

        if (declaration.values) {
            if (Array.isArray(declaration.values)) {
                record.values = declaration.values.map((getter) => resolveValue(node, getter));
            } else {
                record.values = resolveValue(node, declaration.values);
            }
        }

        result.properties[name] = record;
    }

    for (const name in this.properties) {
        if (result.properties[name]) {
            continue;
        }

        const record = {
            target: this.properties[name]
        };

        result.properties[name] = record;
    }

    for (const name in this.components) {
        const component = node && ComponentMap.get(node, Prefab.COMPONENTS[name]);

        const records = {};

        for (const key in Prefab.PROPERTIES[name]) {
            const declaration = Prefab.PROPERTIES[name][key];
            const record = {
                target: this.components[name][key]
            };

            if (record.target === undefined && declaration.default !== undefined) {
                record.target = resolveValue(component, declaration.default);
            }

            if (record.target === undefined && declaration.editorDefault !== undefined) {
                record.target = resolveValue(component, declaration.editorDefault);
            }

            if (declaration.getter && component) {
                record.actual = declaration.getter(component);
            }

            if (declaration.values) {
                if (Array.isArray(declaration.values)) {
                    record.values = declaration.values.map((getter) => resolveValue(component, getter));
                } else {
                    record.values = resolveValue(component, declaration.values);
                }
            }

            records[key] = record;
        }

        for (const key in this.components[name]) {
            if (!records[key]) {
                records[key] = {
                    target: this.components[name][key]
                };
            }
        }

        result.components[name] = records;
    }

    for (const name in this.params) {
        result.params[name] = {
            target: this.params[name]
        };
    }

    return result;
};

ShadowNode.prototype.setParameter = function (parameterName, value) {
    if (value === undefined || value === null) {
        delete this.params[parameterName];
    } else {
        this.params[parameterName] = value;
    }
};

ShadowNode.prototype.setProperty = function (propertyName, value) {
    let property;

    if (value === undefined || value === null) {
        value = undefined;
    }

    return Promise.resolve()
        .then(() => {
            const properties = Prefab.PROPERTIES[this.type];

            property = properties[propertyName];
            if (!property) {
                throw new Error(`unknown property "${propertyName}"`);
            }
        })
        .then(() => {
            const bundleName = property.type === "asset" && cleverapps.prefabEditor.assets.getRealBundleName(this.properties[propertyName]);
            if (bundleName) {
                return cleverapps.prefabEditor.assets.unloadBundle(bundleName);
            }
        })
        .then(() => {
            const bundleName = property.type === "asset" && cleverapps.prefabEditor.assets.getRealBundleName(value);
            if (bundleName) {
                return cleverapps.prefabEditor.assets.loadBundle(bundleName);
            }
        })
        .then(() => {
            if (value === undefined) {
                delete this.properties[propertyName];

                this.rebuild();
                return;
            }

            this.properties[propertyName] = value;

            if (property.setter) {
                this.nodes.forEach((node) => PrefabBuilder.applyProperties(node, this.type, this.properties, ComponentMap.get(node, ShadowNodeBridge).getContext()));
            } else {
                this.rebuild();
            }
        });
};

ShadowNode.prototype.setComponentProperty = function (componentName, propertyName, value) {
    let property;

    if (value === undefined || value === null) {
        value = undefined;
    }

    return Promise.resolve()
        .then(() => {
            if (!this.components[componentName]) {
                throw new Error(`unknown component "${componentName}"`);
            }

            const properties = Prefab.PROPERTIES[componentName] || {};

            property = properties[propertyName];
            if (!property) {
                throw new Error(`unknown property "${propertyName}"`);
            }
        })
        .then(() => {
            const bundleName = property.type === "asset" && cleverapps.prefabEditor.assets.getRealBundleName(this.components[componentName][propertyName]);
            if (bundleName) {
                return cleverapps.prefabEditor.assets.unloadBundle(bundleName);
            }
        })
        .then(() => {
            const bundleName = property.type === "asset" && cleverapps.prefabEditor.assets.getRealBundleName(value);
            if (bundleName) {
                return cleverapps.prefabEditor.assets.loadBundle(bundleName);
            }
        })
        .then(() => {
            if (value === undefined) {
                delete this.components[componentName][propertyName];

                this.rebuild();
                return;
            }

            this.components[componentName][propertyName] = value;

            if (property.setter) {
                this.nodes.forEach((node) => {
                    const component = ComponentMap.get(node, Prefab.COMPONENTS[componentName]);
                    if (component) {
                        PrefabBuilder.applyProperties(component, componentName, this.components[componentName], ComponentMap.get(node, ShadowNodeBridge).getContext());
                    }
                });
            } else {
                this.rebuild();
            }
        });
};

ShadowNode.prototype.addComponent = function (name) {
    return Promise.resolve()
        .then(() => {
            if (!Prefab.COMPONENTS[name]) {
                throw new Error(`unknown component "${name}"`);
            }

            if (this.components[name]) {
                throw new Error(`component "${name}" already exists`);
            }

            const config = {};

            const declarations = Prefab.PROPERTIES[name] || {};
            for (const key in declarations) {
                const declarationDefault = declarations[key].default;
                if (declarationDefault !== undefined && typeof declarationDefault !== "function") {
                    config[key] = cleverapps.clone(declarationDefault, true);
                }
            }

            this.components[name] = config;

            this.rebuild();
        });
};

ShadowNode.prototype.removeComponent = function (name) {
    return Promise.resolve()
        .then(() => {
            if (!this.components[name]) {
                throw new Error(`unknown component "${name}"`);
            }

            delete this.components[name];

            this.rebuild();
        });
};

ShadowNode.prototype.addChild = function (childShadowNode, index) {
    if (index === undefined || index === null || index < 0 || index > this.children.length) {
        index = this.children.length;
    }

    this.children.splice(index, 0, childShadowNode.shadowNodeId);

    childShadowNode.parent = this.shadowNodeId;

    this.listNodes().slice().forEach((parentNode) => {
        const node = PrefabBuilder.buildNode(childShadowNode.serializeContent(), ComponentMap.get(parentNode, ShadowNodeBridge).getContext());

        const children = parentNode.getChildren().slice();
        children.forEach((childNode) => childNode.removeTemporarily(false));

        children.push(node);
        children.sort((a, b) => {
            const bridgeA = ComponentMap.get(a, ShadowNodeBridge);
            const indexA = !bridgeA ? -1 : this.children.indexOf(bridgeA.getShadowNode().shadowNodeId);

            const bridgeB = ComponentMap.get(b, ShadowNodeBridge);
            const indexB = !bridgeB ? -1 : this.children.indexOf(bridgeB.getShadowNode().shadowNodeId);

            return indexA === -1 || indexB === -1 ? 0 : indexA - indexB;
        });

        children.forEach((childNode) => {
            parentNode.addChild(childNode);
        });
    });

    return [
        { path: `content.${childShadowNode.shadowNodeId}.parent`, value: this.shadowNodeId },
        { path: `content.${this.shadowNodeId}.children`, value: this.serializeContent().children }
    ];
};

ShadowNode.prototype.removeChild = function (childShadowNode) {
    const index = this.children.indexOf(childShadowNode.shadowNodeId);
    if (index === -1) {
        return [];
    }

    this.children.splice(index, 1);

    childShadowNode.parent = null;

    childShadowNode.listNodes().slice().forEach((node) => {
        node.removeFromParent();
    });

    return [
        { path: `content.${childShadowNode.shadowNodeId}.parent`, value: null },
        { path: `content.${this.shadowNodeId}.children`, value: this.serializeContent().children }
    ];
};

ShadowNode.prototype.serializeContent = function () {
    const config = {
        ...this.properties
    };

    if (!cleverapps.isEmpty(this.components)) {
        config.components = { ...this.components };
    }

    config.shadowNodeId = this.shadowNodeId;

    config.parent = this.parent;
    config.children = this.children;

    return config;
};

ShadowNode.prototype.serializeParams = function () {
    return this.params;
};
